import React from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import { translate } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Avatar, Button, TextButton, Text, Collapse } from 'wix-style-react';
import ChevronDown from 'wix-ui-icons-common/ChevronDown';
import ChevronUp from 'wix-ui-icons-common/ChevronUp';
import ChevronLeft from 'wix-ui-icons-common/ChevronLeft';
import classNames from 'classnames';
import s from './DetailsCard.scss';
import sInfoItem from './InfoItem.scss';
import ReviewStars from '../../common/ReviewStars/ReviewStars';
import EditProfileLink from '../../common/EditProfileLink/EditProfileLink';
import SocialLinks from './SocialLinks/SocialLinks';
import ExpertLanguages from './ExpertLanguages/ExpertLanguages';
import expertLocation from '../../../utils/expertLocationFormatter';
import {
  ARENA_EXPLORE_URL,
  ExpertAvailablityStatus,
  PageWidthBreakPoint,
} from '../../../utils/constants';
import { ServicesProvider } from '@wix/experts-common';
import isServer from '../../../utils/isServer';

const hasLinkedAccounts = expertDetails => {
  if (expertDetails) {
    return (
      !_isEmpty(
        Object.values(expertDetails.socials).filter(social => !!social),
      ) || !!expertDetails.website
    );
  }
  return false;
};

class DetailsCard extends React.Component {
  constructor(props) {
    super(props);
    const showMore = isServer
      ? true
      : window.matchMedia(PageWidthBreakPoint.Width_1024).matches;
    this.state = {
      showMore,
    };
  }

  static propTypes = {
    t: PropTypes.func,
    expert: PropTypes.object,
    possibleLanguages: PropTypes.array,
    isMobile: PropTypes.bool,
    onContactMeClicked: PropTypes.func,
    isMyProfile: PropTypes.bool,
  };

  static defaultProps = {
    isMyProfile: false,
  };

  handleSocialLinkClicked = socialName => {
    this.context.services.withBI({
      evid: 30,
      typeid: 1,
      proGuid: this.props.expert.id,
      communicationOption: socialName,
    });
  };

  handleContactMeClicked = () => {
    this.context.services.withBI(
      {
        evid: 31,
        typeid: 1,
        proGuid: this.props.expert.id,
        origin: 'expert profile',
        originLocation: 'details card',
        reviewsScore: this.props.expert.reviewScores
          ? this.props.expert.reviewScores.score
          : 0,
      },
      this.props.onContactMeClicked,
    );
  };

  render() {
    const {
      t,
      history,
      expert,
      isMobile,
      isMyProfile,
      possibleLanguages,
    } = this.props;
    const { showMore } = this.state;
    const { isInModal } = this.context.services;
    const isExpertUnAvailable =
      expert.status === ExpertAvailablityStatus.UnAvailable;
    const reviewsScore = expert.reviewScores ? expert.reviewScores.score : 0;
    const totalReviews = expert.reviewScores ? expert.reviewScores.count : 0;
    const totalProjects = expert.scores
      ? parseInt(expert.scores.projects, 10)
      : 0;
    const isMatchMedia1024 = isServer
      ? true
      : window.matchMedia(PageWidthBreakPoint.Width_1024).matches;

    return (
      <div
        className={classNames(
          s.detailsCardContainer,
          isMobile ? s.mobile : s.desktop,
        )}
      >
        <div className={s.details}>
          <div className={s.baseInfo}>
            {!isMyProfile && !isInModal && (
              <div className={s.breadcrumbsWrapper}>
                <Link
                  to={ARENA_EXPLORE_URL}
                  onClick={e => {
                    e.preventDefault();
                    history.goBack();
                  }}
                  style={{ textDecoration: 'none' }}
                >
                  <TextButton
                    skin="dark"
                    prefixIcon={<ChevronLeft />}
                    tabIndex={-1}
                  >
                    {t('client-expertview-card-back')}
                  </TextButton>
                </Link>
              </div>
            )}
            <div className={s.basicTop}>
              <div className={s.basicInfo} data-hook="expert-base-details">
                <div
                  className={classNames(s.expertAvatarWrapper, {
                    [s.myProfile]: isMyProfile && !isInModal,
                  })}
                  data-hook="expert-card-avatar-wrapper"
                >
                  <Avatar
                    className={s.expertAvatar}
                    dataHook="expert-card-avatar"
                    color="A1"
                    imgProps={{
                      src: expert.personalDetails.profilePhoto,
                    }}
                    name={expert.personalDetails.name}
                    size="size72"
                  />
                  {isMyProfile && !isInModal && (
                    <div
                      className={s.editProfileLinkWrapper}
                      data-hook="expert-card-edit-profile-link"
                    >
                      <EditProfileLink tabName="info" />
                    </div>
                  )}
                </div>
                <div className={s.generalInfo}>
                  <Text
                    weight="bold"
                    size="medium"
                    secondary={false}
                    light={false}
                    dataHook="expert-card-name"
                    ellipsis={true}
                  >
                    {expert.personalDetails.name}
                  </Text>
                  <Text
                    tagName="div"
                    weight="normal"
                    size="small"
                    secondary={true}
                    light={false}
                    dataHook="expert-card-short-description"
                    className={s.desc}
                  >
                    {expert.personalDetails.shortDescription}
                  </Text>
                  <div
                    className={s.expertScore}
                    data-hook="expert-card-reviews-score-stars"
                  >
                    {totalReviews > 0 && (
                      <span>
                        <ReviewStars
                          fullStars={reviewsScore}
                          dataHook="expert-card-stars-score"
                        />
                        <Text
                          weight="thin"
                          size="small"
                          secondary={true}
                          light={false}
                          className={s.expertScoreText}
                          dataHook="expert-card-total-reviews"
                        >
                          {t('client-expertview-card-reviews', {
                            count: totalReviews,
                          })}
                        </Text>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {!isExpertUnAvailable && !isMyProfile && !isInModal && (
                <div className={s.contactMeButtonWrapper}>
                  <Button
                    className={s.contactMeButton}
                    dataHook="expert-card-get-quote-button"
                    onClick={this.handleContactMeClicked}
                    priority="primary"
                    size={isMatchMedia1024 ? 'medium' : 'large'}
                    skin="standard"
                  >
                    {t('client-expertview-card-CTA')}
                  </Button>
                </div>
              )}
            </div>
            <TextButton
              className={s.showMoreButton}
              dataHook="show-more-button"
              as="button"
              onClick={() => {
                this.setState({ showMore: !showMore });
              }}
              skin="dark"
              suffixIcon={!showMore ? <ChevronDown /> : <ChevronUp />}
              weight="normal"
            >
              {!showMore
                ? t('client-expertview-card-show-more')
                : t('client-expertview-card-show-less')}
            </TextButton>
          </div>
          <Collapse open={showMore}>
            <div className={s.moreInfo}>
              <div className={s.seperator} />
              <div className={classNames(s.info, s.section)}>
                <Text
                  weight="bold"
                  size="small"
                  tagName="div"
                  className={s.sectionTitle}
                >
                  {t('client-expertview-card-info')}
                </Text>
                <div
                  className={sInfoItem.infoItem}
                  data-hook="expert-card-location"
                >
                  <div
                    className={classNames(
                      sInfoItem.infoIcon,
                      sInfoItem.iconLocation,
                    )}
                  />
                  <Text
                    size="small"
                    secondary={false}
                    light={false}
                    tagName="div"
                    weight="thin"
                    className={sInfoItem.infoText}
                  >
                    {expertLocation(expert.personalDetails)}
                  </Text>
                </div>
                <div
                  className={sInfoItem.infoItem}
                  data-hook="expert-card-languages"
                >
                  <div
                    className={classNames(
                      sInfoItem.infoIcon,
                      sInfoItem.iconLanguage,
                    )}
                  />
                  <Text
                    size="small"
                    secondary={false}
                    light={false}
                    tagName="div"
                    weight="thin"
                    className={sInfoItem.infoText}
                  >
                    <ExpertLanguages
                      expert={expert}
                      possibleLanguages={possibleLanguages}
                    />
                  </Text>
                </div>
                <div
                  className={sInfoItem.infoItem}
                  data-hook="expert-card-projects"
                >
                  <div
                    className={classNames(
                      sInfoItem.infoIcon,
                      sInfoItem.iconProjects,
                    )}
                  />
                  <Text
                    size="small"
                    secondary={false}
                    light={false}
                    tagName="div"
                    weight="thin"
                    className={sInfoItem.infoText}
                  >
                    {t('client-expertview-card-projects', {
                      count: totalProjects,
                    })}
                  </Text>
                </div>
              </div>
              <div className={s.seperator} />
              {hasLinkedAccounts(expert.personalDetails) && (
                <div
                  className={classNames(s.linkedAccounts, s.section)}
                  data-hook="expert-card-linked-accounts"
                >
                  <Text
                    weight="bold"
                    size="small"
                    tagName="div"
                    className={s.sectionTitle}
                  >
                    {t('client-expertview-card-linkedaccounts')}
                  </Text>
                  {expert.personalDetails.website !== '' && (
                    <div
                      className={sInfoItem.infoItem}
                      data-hook="expert-card-linked-account-item"
                    >
                      <div
                        className={classNames(
                          sInfoItem.infoIcon,
                          sInfoItem.iconLink,
                        )}
                      />
                      <div className={sInfoItem.infoText}>
                        <a
                          href={expert.personalDetails.website}
                          onClick={() =>
                            this.handleSocialLinkClicked('website')
                          }
                          data-hook="expert-card-linked-account-item-link"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Text
                            size="small"
                            secondary={false}
                            light={false}
                            tagName="span"
                            weight="thin"
                          >
                            {t('client-expertview-card-site')}
                          </Text>
                        </a>
                      </div>
                    </div>
                  )}
                  <SocialLinks
                    expertDetails={expert.personalDetails}
                    onLinkClicked={this.handleSocialLinkClicked}
                  />
                </div>
              )}
            </div>
          </Collapse>
        </div>
      </div>
    );
  }
}

DetailsCard.contextTypes = ServicesProvider.childContextTypes;
export default translate()(DetailsCard);
